import { useContextOrThrow } from '@deephaven/react-hooks';
import { createContext } from 'react';

/**
 * Descriptor for a variable by name. Used when needed to fetch an object.
 */

/**
 * Descriptor for a variable by id. Used when needed to fetch an object.
 */

export function isNameVariableDescriptor(value) {
  return typeof value === 'object' && value != null && typeof value.type === 'string' && value.name != null;
}
export function isIdVariableDescriptor(value) {
  return typeof value === 'object' && value != null && typeof value.type === 'string' && value.id != null;
}
export function isVariableDescriptor(value) {
  return isNameVariableDescriptor(value) || isIdVariableDescriptor(value);
}

/**
 * Function to fetch an object based on a provided descriptor object.
 * @param descriptor Descriptor object or URI to fetch the object from. Can be extended by a specific implementation to
 *                   include additional fields (such as a session ID) to uniquely identify an object.
 */

export var ObjectFetcherContext = /*#__PURE__*/createContext(null);
ObjectFetcherContext.displayName = 'ObjectFetcherContext';

/**
 * Gets a descriptor that only has the ID or name set, but not both.
 * API will throw an error if both are set when fetching from the connection.
 * @param descriptor Variable descriptor to sanitize
 * @returns Descriptor object that has either the ID or name set, but not both.
 */
export function sanitizeVariableDescriptor(descriptor) {
  // Can't use a spread operator because of how the GWT compiled code defines properties on the object: https://github.com/gwtproject/gwt/issues/9913
  if (isIdVariableDescriptor(descriptor)) {
    return {
      id: descriptor.id,
      type: descriptor.type
    };
  }
  if (isNameVariableDescriptor(descriptor)) {
    var _descriptor$name;
    return {
      name: (_descriptor$name = descriptor.name) !== null && _descriptor$name !== void 0 ? _descriptor$name : '',
      type: descriptor.type
    };
  }
  throw new Error("Invalid descriptor: ".concat(descriptor));
}

/**
 * Get the variable descriptor for a definition.
 * @param definition Definition to get the variable descriptor from
 * @returns Serializable VariableDescriptor object
 */
export function getVariableDescriptor(definition) {
  var _definition$type, _definition$title;
  return {
    type: (_definition$type = definition.type) !== null && _definition$type !== void 0 ? _definition$type : '',
    name: (_definition$title = definition.title) !== null && _definition$title !== void 0 ? _definition$title : definition.name,
    id: definition.id
  };
}

/**
 * Use a function to fetch an object based on provided metadata
 * @returns Function to asynchronously fetch an object based on provided metadata
 */
export function useObjectFetcher() {
  return useContextOrThrow(ObjectFetcherContext, 'No ObjectFetcher available in useObjectFetcher. Was code wrapped in ObjectFetcherContext.Provider?');
}
//# sourceMappingURL=useObjectFetcher.js.map